import { buildCoursePath } from './buildCoursePath';
import { ScormChameleonUtils, ScormRiseUtils } from './scormUtils';

/**
 * A utility class useful for helping to determine things about a
 * particular learning object. Use of this class is entirely optional.
 */
export class LearningObjectUtils {

    /**
     * The possible states of a learning object. A learning object is one of
     * the items that make up a course. It can either be a SCORM object or a
     * video. This state represents the user's progress through the learning
     * object.
     */
    static status = {
        ASSIGNED: 'ASSIGNED',
        IN_PROGRESS: 'IN_PROGRESS',
        COMPLETED: 'COMPLETED',
    };

    /**
     * The format of the learning object.
     */
    static format = {
        VIDEO: 'video',
        SCORM: 'scorm',
    };

    /**
     * Returns whether or not the provided learning object is considered to be
     * assigned to the user.
     * 
     * @param { object } learningObjectData - The learning object data.
     * @returns { boolean }
     */
    static isAssigned( learningObjectData ) {
        return learningObjectData?.assignment?.status === this.status.ASSIGNED;
    }

    /**
     * Returns whether or not the provided learning object is considered to be
     * in progress by the user.
     * 
     * @param { object } learningObjectData - The learning object data.
     * @returns { boolean }
     */
    static isInProgress( learningObjectData ) {
        return learningObjectData?.assignment?.status === this.status.IN_PROGRESS;
    }

    /**
     * Returns whether or not the provided learning object is considered to be
     * completed by the user.
     * 
     * @param { object } learningObjectData - The learning object data.
     * @returns { boolean }
     */
    static isComplete( learningObjectData ) {
        return learningObjectData?.assignment?.status === this.status.COMPLETED;
    }

    /**
     * Returns whether or not the provided learning object is considered to be
     * in SCORM format.
     * 
     * @param { object } learningObjectData - The learning object data.
     * @returns { boolean }
     */
    static isScorm( learningObjectData ) {
        // TODO: Once the video format is supported, uncomment this line.
        // return ( learningObjectData.format === this.format.SCORM );

        // TODO: Remove this mimetype check once video format is supported.
        const mimetype = learningObjectData.content?.mimetype;
        if ( mimetype ) {
            return ( mimetype.indexOf( 'application/zip' ) !== -1 );
        }
        
        // Default to true if no mimetype is provided.
        return true;
    }

    /**
     * Returns whether or not the provided learning object is considered to be
     * in video format.
     * 
     * @param { object } learningObjectData - The learning object data.
     * @returns { boolean }
     */
    static isVideo( learningObjectData ) {
        // TODO: Once the video format is supported, uncomment this line.
        // return ( learningObjectData.format === this.format.VIDEO );

        // TODO: Remove this mimetype check once video format is supported.
        const mimetype = learningObjectData.content?.mimetype;
        if ( mimetype ) {
            return ( mimetype.indexOf( 'video/' ) !== -1 );
        }
        return false;
    }

    /**
     * Returns whether or not the provided learning object is considered to be
     * a Chameleon SCORM course.
     * 
     * @param { object } learningObjectData - The learning object data.
     * @returns { boolean }
     */
    static isScormChameleon( learningObjectData ) {
        return this.isScorm( learningObjectData )
            ? ScormChameleonUtils.isValidEntryPath( learningObjectData?.content?.url )
            : false;
    }

    /**
     * Returns whether or not the provided learning object is considered to be
     * a Rise SCORM course.
     * 
     * @param { object } learningObjectData - The learning object data.
     * @returns { boolean }
     */
    static isScormRise( learningObjectData ) {
        return this.isScorm( learningObjectData )
            ? ScormRiseUtils.isValidEntryPath( learningObjectData?.content?.url )
            : false;
    }

    /**
     * Returns the full launch URL for the learning object.
     * 
     * @param { object } learningObjectData  - The learning object data.
     * @param { object } options - Options to pass in to adjust the resulting URL.
     * @returns { string }
     */
    static getLaunchUrl( learningObjectData, { localeCode, themeKey } = {} ) {
        const contentUrl = learningObjectData?.content?.url;
        if ( this.isScormChameleon( learningObjectData ) ) {
            const entryPath = ScormChameleonUtils.buildEntryPath( contentUrl, { localeCode, themeKey } );
            return buildCoursePath( entryPath );
        }
        else if ( this.isScormRise( learningObjectData ) ) {
            const entryPath = ScormRiseUtils.buildEntryPath( contentUrl, { localeCode } );
            return buildCoursePath( entryPath );
        }
        else {
            return buildCoursePath( contentUrl );
        }
    }

}
